import type { BatchDocResult } from './use_multi_doc_upload';

export interface FieldCandidate {
  value: string;
  displayValue: string;
  sourceFilename: string;
  uploadId: number;
  isInferred: boolean;
}

export interface ConflictField {
  fieldName: string;
  candidates: FieldCandidate[];
  selectedIndex: number;
}

export interface FailedDocument {
  uploadId: number;
  filename: string | null;
  errorMessage: string;
}

export interface MergeResult {
  mergedFormData: Record<string, string>;
  conflicts: ConflictField[];
  populatedFieldNames: string[];
  inferredFields: string[];
  allIngestMetadata: IngestMetadata[];
  failedDocuments: FailedDocument[];
  ingestReviewId: number | null;
}

export interface IngestMetadata {
  ai_extracted_fields: Record<string, unknown>;
  inferred_fields: string[];
  filename: string;
  upload_id: number;
  ai_model: string;
}

// Normalize a value for comparison: coerces to string, strips formatting, lowercases
function normalize(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const str = String(value).trim().toLowerCase();
  const numeric = parseFloat(str.replace(/[,$%]/g, ''));
  if (!isNaN(numeric) && str.replace(/[,$%.\d]/g, '') === '') {
    return String(numeric);
  }
  return str;
}

function toDisplayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/**
 * TODO: The merge computation should be moved to the backend.
 * When interactive resolution is added, the frontend would just render the candidates the backend delivers to it.
 */
export function mergeDocumentResults(
  results: BatchDocResult[],
  ingestReviewId: number | null = null,
  filenameByUploadId: Record<number, string> = {}
): MergeResult {
  const successResults = results.filter(r => r.status === 'success' && r.data);
  const failedDocuments: FailedDocument[] = results
    .filter(r => r.status === 'error')
    .map(r => ({
      uploadId: r.upload_id,
      filename: r.filename ?? filenameByUploadId[r.upload_id] ?? null,
      errorMessage: r.error?.message ?? 'Processing failed',
    }));

  if (successResults.length === 0) {
    return {
      mergedFormData: {},
      conflicts: [],
      populatedFieldNames: [],
      inferredFields: [],
      allIngestMetadata: [],
      failedDocuments,
      ingestReviewId,
    };
  }

  // Collect all field names across all docs
  const allFieldNames = new Set<string>();
  successResults.forEach((r) => {
    Object.keys(r.data?.propertyFormData ?? {}).forEach(k => allFieldNames.add(k));
  });

  const mergedFormData: Record<string, string> = {};
  const conflicts: ConflictField[] = [];
  const populatedFieldNamesSet = new Set<string>();
  const inferredFieldsSet = new Set<string>();

  // Build ingest metadata array
  const allIngestMetadata: IngestMetadata[] = successResults.map(r => ({
    ai_extracted_fields: r.data?.propertyFormData ?? {},
    inferred_fields: r.data?.inferredFields ?? [],
    filename: r.data?.uploadMetadata?.filename ?? '',
    upload_id: r.upload_id,
    ai_model: r.data?.uploadMetadata?.aiModel ?? '',
  }));

  // Union of populated/inferred field names
  successResults.forEach((r) => {
    (r.data?.populatedFieldNames ?? []).forEach(f => populatedFieldNamesSet.add(f));
    (r.data?.inferredFields ?? []).forEach(f => inferredFieldsSet.add(f));
  });

  allFieldNames.forEach((fieldName) => {
    const candidates: FieldCandidate[] = [];

    successResults.forEach((r) => {
      const raw = r.data?.propertyFormData?.[fieldName];
      if (raw === null || raw === undefined || raw === '') return;

      const value = String(raw);
      const isInferred = (r.data?.inferredFields ?? []).includes(fieldName);
      candidates.push({
        value,
        displayValue: toDisplayValue(value),
        sourceFilename: r.data?.uploadMetadata?.filename ?? `Document ${r.upload_id}`,
        uploadId: r.upload_id,
        isInferred,
      });
    });

    if (candidates.length === 0) return;

    if (candidates.length === 1) {
      mergedFormData[fieldName] = candidates[0].value;
      return;
    }

    const uniqueNormalized = new Set(candidates.map(c => normalize(c.value)));

    if (uniqueNormalized.size === 1) {
      const definitive = candidates.find(c => !c.isInferred) || candidates[0];
      mergedFormData[fieldName] = definitive.value;
      return;
    }

    // Genuine conflict — populate with the first non-blank (preferring non-inferred) value for now, but still
    // record the candidates so interactive conflict resolution can be wired up later.
    const defaultIndex = candidates.findIndex(c => !c.isInferred);
    const selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;
    mergedFormData[fieldName] = candidates[selectedIndex].value;
    conflicts.push({
      fieldName,
      candidates,
      selectedIndex,
    });
  });

  return {
    mergedFormData,
    conflicts,
    populatedFieldNames: Array.from(populatedFieldNamesSet),
    inferredFields: Array.from(inferredFieldsSet),
    allIngestMetadata,
    failedDocuments,
    ingestReviewId,
  };
}

